import { useLazyQuery, useQuery } from "@apollo/client";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import {
  GetProfileCandidateMeetingsQueryQuery,
  GetProfileCandidateMeetingsQueryQueryVariables,
  GetProfileOneononeQueryQuery,
  GetProfileOneononeQueryQueryVariables,
} from "types/graphql-schema";

import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import { assertEdgesNonNull } from "@helpers/helpers";

import getProfileCandidateMeetingsQuery from "../graphql/get-profile-candidate-meetings-query";
import getProfileOneononeQuery from "../graphql/get-profile-oneonone-query";
import ProfileLastOneononeCard from "./profile-last-oneonone-card";
import ProfileNextOneononeCard from "./profile-next-oneonone-card";
import ProfileNoOneononeSetup from "./profile-no-oneonone-setup";

const ProfileOneononeCards = ({
  profileId,
  profileName,
  profileFirstName,
  profileEmail,
  onLoaded,
}: {
  profileId: number;
  profileName: string;
  profileFirstName: string;
  profileEmail: string;
  onLoaded?: () => void;
}) => {
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const startOfHour = useMemo(() => moment().startOf("hour").format(), []);
  const participants = useMemo(
    () => uniq([currentUser.id, profileId]),
    [currentUser.id, profileId],
  );
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const { data, loading } = useQuery<
    GetProfileOneononeQueryQuery,
    GetProfileOneononeQueryQueryVariables
  >(getProfileOneononeQuery, {
    variables: {
      participants,
      nextAfterDate: startOfHour,
      lastBeforeDate: startOfHour,
    },
    skip: !profileId || !organization.featureFlags.meetings,
  });

  const nextOneonone = data?.nextOneonone?.edges?.[0]?.node;
  const lastOneonone = data?.lastOneonone?.edges?.[0]?.node;

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const managers = assertEdgesNonNull(currentUser.managers);
  const isDirectReportOrManager =
    directReports.some(({ id }) => id === profileId) ||
    managers.some(({ id }) => id === profileId);

  const needsCandidateMeetings =
    !loading &&
    data &&
    !nextOneonone &&
    currentUser.id !== profileId &&
    isDirectReportOrManager;

  const [
    fetchCandidates,
    {
      data: candidateData,
      loading: candidateLoading,
      called: candidatesCalled,
    },
  ] = useLazyQuery<
    GetProfileCandidateMeetingsQueryQuery,
    GetProfileCandidateMeetingsQueryQueryVariables
  >(getProfileCandidateMeetingsQuery);

  useEffect(() => {
    if (needsCandidateMeetings) {
      fetchCandidates({
        variables: {
          participants,
          nextAfterDate: startOfHour,
        },
      });
    }
  }, [needsCandidateMeetings, fetchCandidates, participants, startOfHour]);

  // Signal parent when done loading (either skipped, loaded, or candidates loaded).
  // When needsCandidateMeetings is true, we must wait until fetchCandidates has
  // been called (via the effect) AND finished loading — otherwise candidateLoading
  // is false before the request is even dispatched, causing a premature onLoaded.
  const isFullyLoaded =
    !organization.featureFlags.meetings ||
    (!loading &&
      (!needsCandidateMeetings || (candidatesCalled && !candidateLoading)));

  useEffect(() => {
    if (isFullyLoaded) {
      onLoadedRef.current?.();
    }
  }, [isFullyLoaded]);

  if (!organization.featureFlags.meetings) {
    return null;
  }

  if (
    loading ||
    (needsCandidateMeetings && (!candidatesCalled || candidateLoading))
  ) {
    return <Loading />;
  }

  const candidateMeetings = candidateData?.candidateMeetings
    ? assertEdgesNonNull(candidateData.candidateMeetings)
    : [];

  // Deduplicate by meetingGroupId to get unique recurring series
  const uniqueCandidates = candidateMeetings.reduce(
    (acc, meeting) => {
      if (!acc.seen.has(meeting.meetingGroupId)) {
        acc.seen.add(meeting.meetingGroupId);
        acc.meetings.push(meeting);
      }
      return acc;
    },
    { seen: new Set<number>(), meetings: [] as typeof candidateMeetings },
  ).meetings;

  return (
    <div className="flex flex-col gap-6">
      {nextOneonone ? (
        <ProfileNextOneononeCard meeting={nextOneonone} profileId={profileId} />
      ) : currentUser.id !== profileId && isDirectReportOrManager ? (
        <ProfileNoOneononeSetup
          profileId={profileId}
          profileName={profileName}
          profileFirstName={profileFirstName}
          profileEmail={profileEmail}
          candidateMeetings={uniqueCandidates}
        />
      ) : null}
      {lastOneonone && <ProfileLastOneononeCard meeting={lastOneonone} />}
    </div>
  );
};

export default ProfileOneononeCards;
